import { ChevronDown, ChevronRight, Folder, FolderOpen, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { DataLabel } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { cn } from '@/utils/cn'

export interface FolderSelection {
  labelId: string
  labelName: string
  labelColor: string | null
  /** Relative path within the label (e.g. "pdfs" or "raw/2024"). null = label root.
   *  Use this for getLabelFolderTree, browse URL ?path=, and upload relative_path. */
  path: string | null
  /** Full storage path including label prefix (e.g. "lake/green/pdfs"). null = label root.
   *  Use this for search storage_prefixes. */
  storagePath: string | null
  displayPath: string // human readable e.g. "yellow / processed"
}

interface FolderNode {
  id: string
  name: string
  /** Relative path within the label (e.g. "pdfs"). Used for getLabelFolderTree API calls. */
  relativePath: string
  /** Full storage path including label prefix (e.g. "lake/green/pdfs"). Used for FolderSelection.storagePath. */
  fullPath: string
  hasChildren: boolean
  children?: FolderNode[]
  loaded?: boolean
}

interface LabelNode {
  label: DataLabel
  expanded: boolean
  loading: boolean
  folders: FolderNode[]
  loaded: boolean
}

interface LakeFolderPickerSheetProps {
  open: boolean
  onClose: () => void
  labels: DataLabel[]
  onSelect: (selection: FolderSelection) => void
  title?: string
  confirmLabel?: string
}

export function LakeFolderPickerSheet({
  open,
  onClose,
  labels,
  onSelect,
  title = 'Select folder',
  confirmLabel = 'Select',
}: LakeFolderPickerSheetProps) {
  const [labelNodes, setLabelNodes] = useState<LabelNode[]>([])
  const [selected, setSelected] = useState<FolderSelection | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set())

  // Initialise label list whenever labels prop changes
  useEffect(() => {
    setLabelNodes(
      labels.map(label => ({
        label,
        expanded: false,
        loading: false,
        folders: [],
        loaded: false,
      }))
    )
    setSelected(null)
    setExpandedFolders(new Set())
    setLoadingFolders(new Set())
  }, [labels])

  // Toggle a label's expanded state; lazy-load its root folders
  const handleToggleLabel = async (labelId: string) => {
    const node = labelNodes.find(n => n.label.id === labelId)
    if (!node) return

    if (node.expanded) {
      // Collapse
      setLabelNodes(prev => prev.map(n => (n.label.id === labelId ? { ...n, expanded: false } : n)))
      return
    }

    // Expand — load folders if not yet loaded
    setLabelNodes(prev =>
      prev.map(n => (n.label.id === labelId ? { ...n, expanded: true, loading: !n.loaded } : n))
    )

    if (!node.loaded) {
      try {
        const resp = await lakeApi.getLabelFolderTree(labelId)
        setLabelNodes(prev =>
          prev.map(n =>
            n.label.id === labelId
              ? {
                  ...n,
                  loading: false,
                  loaded: true,
                  folders: resp.folders.map(f => ({
                    id: `${labelId}::${f.full_path}`,
                    name: f.name,
                    relativePath: f.path,
                    fullPath: f.full_path,
                    hasChildren: f.has_children,
                    loaded: false,
                  })),
                }
              : n
          )
        )
      } catch {
        setLabelNodes(prev =>
          prev.map(n => (n.label.id === labelId ? { ...n, loading: false } : n))
        )
      }
    }
  }

  // Toggle a folder; lazy-load its children
  const handleToggleFolder = async (labelId: string, folder: FolderNode) => {
    const folderId = folder.id
    const isExpanded = expandedFolders.has(folderId)

    if (isExpanded) {
      setExpandedFolders(prev => {
        const next = new Set(prev)
        next.delete(folderId)
        return next
      })
      return
    }

    setExpandedFolders(prev => new Set([...prev, folderId]))

    if (!folder.loaded && folder.hasChildren) {
      setLoadingFolders(prev => new Set([...prev, folderId]))
      try {
        const resp = await lakeApi.getLabelFolderTree(labelId, folder.relativePath)
        const children: FolderNode[] = resp.folders.map(f => ({
          id: `${labelId}::${f.full_path}`,
          name: f.name,
          relativePath: f.path,
          fullPath: f.full_path,
          hasChildren: f.has_children,
          loaded: false,
        }))

        // Update the folder node in the tree immutably
        setLabelNodes(prev =>
          prev.map(n => {
            if (n.label.id !== labelId) return n
            return {
              ...n,
              folders: patchFolderChildren(n.folders, folderId, children),
            }
          })
        )
      } catch {
        // ignore
      } finally {
        setLoadingFolders(prev => {
          const next = new Set(prev)
          next.delete(folderId)
          return next
        })
      }
    }
  }

  const handleSelectLabel = (label: DataLabel) => {
    setSelected({
      labelId: label.id,
      labelName: label.name,
      labelColor: label.color,
      path: null,
      storagePath: null,
      displayPath: label.name,
    })
  }

  const handleSelectFolder = (label: DataLabel, folder: FolderNode) => {
    setSelected({
      labelId: label.id,
      labelName: label.name,
      labelColor: label.color,
      path: folder.relativePath,
      storagePath: folder.fullPath,
      displayPath: `${label.name} / ${folder.relativePath.replace(/\//g, ' / ')}`,
    })
  }

  const handleConfirm = () => {
    if (!selected) return
    onSelect(selected)
    onClose()
  }

  return (
    <Sheet
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:max-w-[360px] p-0 flex flex-col"
        showCloseButton={true}
      >
        <SheetHeader className="border-b border-neutral-800 px-4 py-3">
          <SheetTitle className="text-sm font-medium">{title}</SheetTitle>
          {selected && (
            <p className="text-xs text-neutral-400 truncate mt-0.5">
              Selected: <span className="text-white">{selected.displayPath}</span>
            </p>
          )}
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {labelNodes.length === 0 && (
              <div className="text-xs text-neutral-500 text-center py-8">No labels available</div>
            )}

            {labelNodes.map(node => (
              <div key={node.label.id}>
                <button
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-neutral-800/60 transition-colors',
                    selected?.labelId === node.label.id && selected.path === null
                      ? 'bg-neutral-800 text-white'
                      : 'text-neutral-300'
                  )}
                  onClick={() => handleSelectLabel(node.label)}
                >
                  <button
                    className="flex-shrink-0 p-0.5 hover:bg-neutral-700 rounded"
                    onClick={e => {
                      e.stopPropagation()
                      handleToggleLabel(node.label.id)
                    }}
                  >
                    {node.loading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-500" />
                    ) : node.expanded ? (
                      <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-neutral-500" />
                    )}
                  </button>
                  {node.label.color ? (
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: node.label.color }}
                    />
                  ) : (
                    <FolderOpen className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
                  )}
                  <span className="font-medium truncate">{node.label.name}</span>
                </button>

                {node.expanded && (
                  <div className="ml-6">
                    {node.folders.length === 0 && node.loaded && (
                      <div className="text-xs text-neutral-600 px-2 py-1">No subfolders</div>
                    )}
                    {node.folders.map(folder => (
                      <FolderRow
                        key={folder.id}
                        folder={folder}
                        label={node.label}
                        depth={0}
                        expandedFolders={expandedFolders}
                        loadingFolders={loadingFolders}
                        selected={selected}
                        onToggle={f => handleToggleFolder(node.label.id, f)}
                        onSelect={f => handleSelectFolder(node.label, f)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <SheetFooter className="border-t border-neutral-800 px-4 py-3 flex-row gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="flex-1 border-neutral-700 text-neutral-300"
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={!selected} className="flex-1">
            {confirmLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

// ── Recursive folder row ────────────────────────────────────────────────────

interface FolderRowProps {
  folder: FolderNode
  label: DataLabel
  depth: number
  expandedFolders: Set<string>
  loadingFolders: Set<string>
  selected: FolderSelection | null
  onToggle: (folder: FolderNode) => void
  onSelect: (folder: FolderNode) => void
}

function FolderRow({
  folder,
  label,
  depth,
  expandedFolders,
  loadingFolders,
  selected,
  onToggle,
  onSelect,
}: FolderRowProps) {
  const isExpanded = expandedFolders.has(folder.id)
  const isLoading = loadingFolders.has(folder.id)
  const isSelected = selected?.labelId === label.id && selected.path === folder.relativePath

  return (
    <div style={{ paddingLeft: depth * 12 }}>
      <button
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left hover:bg-neutral-800/60 transition-colors',
          isSelected ? 'bg-neutral-800 text-white' : 'text-neutral-400'
        )}
        onClick={() => onSelect(folder)}
      >
        {folder.hasChildren ? (
          <button
            className="flex-shrink-0 p-0.5 hover:bg-neutral-700 rounded"
            onClick={e => {
              e.stopPropagation()
              onToggle(folder)
            }}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-500" />
            ) : isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-neutral-500" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-neutral-500" />
            )}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0" />
        )}
        <Folder className="h-3.5 w-3.5 flex-shrink-0 text-neutral-500" />
        <span className="truncate">{folder.name}</span>
      </button>

      {isExpanded && folder.children && (
        <div className="ml-4">
          {folder.children.length === 0 ? (
            <div className="text-xs text-neutral-600 px-2 py-1">No subfolders</div>
          ) : (
            folder.children.map(child => (
              <FolderRow
                key={child.id}
                folder={child}
                label={label}
                depth={depth + 1}
                expandedFolders={expandedFolders}
                loadingFolders={loadingFolders}
                selected={selected}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ── Utility: patch a folder node's children deep in the tree ───────────────

function patchFolderChildren(
  folders: FolderNode[],
  targetId: string,
  children: FolderNode[]
): FolderNode[] {
  return folders.map(f => {
    if (f.id === targetId) {
      return { ...f, loaded: true, children }
    }
    if (f.children) {
      return { ...f, children: patchFolderChildren(f.children, targetId, children) }
    }
    return f
  })
}
